import { Capacitor } from '@capacitor/core';
import { getStoreProvider } from './inAppBilling';
import { adtraceService } from './adtraceService';
import { paymentServiceApi } from './api';

// GA4 user property. The value is the Adtrace campaign or "organic" when no
// paid tracker/campaign is found.
const CAMPAIGN_USER_PROPERTY = 'campaign';
const ORGANIC_CAMPAIGN = 'organic';

/**
 * Native Firebase Analytics bridge.
 *
 * The plugin is loaded only on a native platform so the website does not
 * initialize the native analytics implementation.
 */
class AnalyticsService {
  private campaignSyncPromise: Promise<void> | null = null;

  private get isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  async logEvent(name: string, params: Record<string, unknown> = {}): Promise<void> {
    if (!this.isAvailable) {
      return;
    }

    try {
      const { FirebaseAnalytics } = await import('@capacitor-community/firebase-analytics');
      await FirebaseAnalytics.logEvent({ name, params });
    } catch (error) {
      console.warn(`[Analytics] Failed to send event: ${name}`, error);
    }
  }

  async setScreenName(screenName: string): Promise<void> {
    if (!this.isAvailable || !screenName) return;

    try {
      const { FirebaseAnalytics } = await import('@capacitor-community/firebase-analytics');
      await FirebaseAnalytics.setScreenName({ screenName, nameOverride: screenName });
    } catch (error) {
      console.warn(`[Analytics] Failed to set screen name: ${screenName}`, error);
    }
  }

  async setUserId(userId: string | null): Promise<void> {
    if (!this.isAvailable) return;

    try {
      const { FirebaseAnalytics } = await import('@capacitor-community/firebase-analytics');
      await FirebaseAnalytics.setUserId({ userId: userId || '' });
    } catch (error) {
      console.warn('[Analytics] Failed to set user ID', error);
    }
  }

  async setUserIdFromAccessToken(accessToken: string | null): Promise<void> {
    if (!accessToken) {
      await this.setUserId(null);
      return;
    }

    try {
      const payloadPart = accessToken.split('.')[1];
      if (!payloadPart) throw new Error('JWT payload is missing');
      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const payload = JSON.parse(atob(padded)) as { user_id?: string | number };
      const userId = payload.user_id;
      if (userId === undefined || userId === null || String(userId).trim() === '') {
        await this.setUserId(null);
        return;
      }
      await this.setUserId(String(userId));
    } catch (error) {
      console.warn('[Analytics] Failed to extract user ID from access token', error);
      await this.setUserId(null);
    }
  }

  async setStoreProvider(): Promise<void> {
    if (!this.isAvailable) {
      return;
    }

    try {
      const provider = await getStoreProvider();
      if (!provider) {
        console.warn('[Analytics] Store provider is unavailable');
        return;
      }

      const { FirebaseAnalytics } = await import('@capacitor-community/firebase-analytics');
      await FirebaseAnalytics.setUserProperty({
        name: 'store_provider',
        value: provider,
      });
    } catch (error) {
      console.warn('[Analytics] Failed to set store provider', error);
    }
  }

  /**
   * Sends the Adtrace campaign to GA4 as soon as attribution is available.
   * Adtrace may resolve attribution asynchronously after the first launch, so
   * a bounded retry is used before classifying the install as organic.
   */
  async setAdtraceCampaign(): Promise<void> {
    if (!this.isAvailable) return;
    if (this.campaignSyncPromise) return this.campaignSyncPromise;

    this.campaignSyncPromise = (async () => {
      const retryDelaysMs = [0, 250, 750, 1500, 3000, 5000, 8000];

      for (const delayMs of retryDelaysMs) {
        if (delayMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        try {
          const attribution = await adtraceService.getAttribution();
          const campaign = typeof attribution?.campaign === 'string'
            ? attribution.campaign.trim()
            : '';

          if (!campaign) continue;

          const { FirebaseAnalytics } = await import('@capacitor-community/firebase-analytics');
          await FirebaseAnalytics.setUserProperty({
            name: CAMPAIGN_USER_PROPERTY,
            value: campaign.toLowerCase() === ORGANIC_CAMPAIGN ? ORGANIC_CAMPAIGN : campaign,
          });
          console.log('[Analytics] Campaign user property synced:', campaign);
          return;
        } catch (error) {
          // Attribution is optional; analytics must never block app startup.
          console.warn('[Analytics] Failed to sync Adtrace campaign', error);
        }
      }

      // No paid attribution appeared during the retry window, so this install
      // is classified as organic and receives exactly one final value.
      try {
        const { FirebaseAnalytics } = await import('@capacitor-community/firebase-analytics');
        await FirebaseAnalytics.setUserProperty({
          name: CAMPAIGN_USER_PROPERTY,
          value: ORGANIC_CAMPAIGN,
        });
        console.log('[Analytics] Campaign user property synced: organic');
      } catch (error) {
        console.warn('[Analytics] Failed to set organic campaign', error);
      }
    })().finally(() => {
      this.campaignSyncPromise = null;
    });

    return this.campaignSyncPromise;
  }

  async setSubscriptionStatus(isActive: boolean): Promise<void> {
    if (!this.isAvailable) {
      return;
    }

    try {
      const { FirebaseAnalytics } = await import('@capacitor-community/firebase-analytics');
      const status = isActive ? 'active' : 'inactive';
      await FirebaseAnalytics.setUserProperty({
        name: 'subscription_status',
        value: status,
      });
    } catch (error) {
      console.warn('[Analytics] Failed to set subscription status', error);
    }
  }

  async syncSubscriptionStatus(): Promise<void> {
    if (!this.isAvailable) {
      return;
    }

    try {
      const response = await paymentServiceApi.get<{ is_active: boolean }>('/subscriptions/status');
      if (response.success && response.data) {
        await this.setSubscriptionStatus(response.data.is_active);
      }
    } catch (error) {
      // Do not mark a user inactive when the status request itself fails.
      console.warn('[Analytics] Failed to sync subscription status', error);
    }
  }
}

export const analyticsService = new AnalyticsService();
